import React, { useState, useEffect, useCallback } from 'react';
import styled, { keyframes } from 'styled-components';
import { orderService } from '../services/api';
import { useSocketEvents } from '../hooks/useSocket';

interface OrderItem { id: number; product_name: string; product_price: number; quantity: number; notes?: string; status: string; course: number; course_name?: string; }
interface Order { id: number; table_name: string; room_name: string; status: string; items: OrderItem[]; created_at: string; waiter_name?: string; }

const pulse = keyframes`0%,100%{opacity:1}50%{opacity:.5}`;

const Page = styled.div`max-width: 1200px; margin: 0 auto; display: flex; flex-direction: column; gap: 20px;`;
const Header = styled.div`display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 8px;`;
const Title = styled.h1`font-size: 24px; font-weight: 700; color: #111827; margin: 0;`;
const LiveDot = styled.span`display: inline-flex; align-items: center; gap: 6px; font-size: 13px; color: #16a34a; font-weight: 500;
  &::before { content: ''; width: 8px; height: 8px; border-radius: 50%; background: #16a34a; animation: ${pulse} 2s infinite; }`;

const Grid = styled.div`display: grid; grid-template-columns: repeat(auto-fill, minmax(300px, 1fr)); gap: 16px;`;

const OrderCard = styled.div<{ $hasReady?: boolean }>`
  background: white; border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.08);
  border-top: 3px solid ${p => p.$hasReady ? '#22c55e' : '#f97316'};
  overflow: hidden;
`;
const OrderCardHeader = styled.div`padding: 12px 16px; border-bottom: 1px solid #f3f4f6; display: flex; justify-content: space-between; align-items: flex-start;`;
const TableInfo = styled.div``;
const TableLabel = styled.div`font-size: 16px; font-weight: 700; color: #1f2937;`;
const TableSub = styled.div`font-size: 12px; color: #9ca3af; margin-top: 2px;`;
const TimeLabel = styled.div`font-size: 12px; color: #6b7280; text-align: right;`;

const ItemsBlock = styled.div`padding: 10px 16px; display: flex; flex-direction: column; gap: 6px;`;
const ItemLine = styled.div<{ $status: string }>`
  display: flex; align-items: center; gap: 8px; padding: 8px 10px; border-radius: 8px;
  background: ${p => p.$status === 'ready' ? '#f0fdf4' : p.$status === 'preparing' ? '#fff7ed' : p.$status === 'served' ? '#f9fafb' : '#fefce8'};
  border: 1px solid ${p => p.$status === 'ready' ? '#bbf7d0' : p.$status === 'preparing' ? '#fed7aa' : p.$status === 'served' ? '#e5e7eb' : '#fde68a'};
  opacity: ${p => p.$status === 'served' ? 0.5 : 1};
`;
const ItemQty = styled.span`font-size: 15px; font-weight: 800; color: #1f2937; min-width: 22px;`;
const ItemName = styled.span`flex: 1; font-size: 14px; color: #1f2937; font-weight: 500;`;
const StatusBtn = styled.button<{ $status: string }>`
  padding: 4px 10px; border-radius: 20px; border: none; font-size: 12px; font-weight: 600; cursor: pointer;
  background: ${p => p.$status === 'ready' ? '#22c55e' : p.$status === 'preparing' ? '#f97316' : p.$status === 'served' ? '#9ca3af' : '#fbbf24'};
  color: white; white-space: nowrap;
  &:hover { opacity: 0.85; }
`;

const CourseRow = styled.div`
  display: flex; align-items: center; justify-content: space-between;
  margin-top: 10px; margin-bottom: 4px;
`;
const CourseLabel = styled.div`font-size: 11px; font-weight: 700; color: #9ca3af; text-transform: uppercase; letter-spacing: 0.5px;`;
const CourseBtn = styled.button<{ $next: string }>`
  padding: 4px 12px; border-radius: 20px; border: none; font-size: 12px; font-weight: 700; cursor: pointer;
  background: ${p => p.$next === 'preparing' ? '#fbbf24' : p.$next === 'ready' ? '#22c55e' : '#9ca3af'};
  color: white;
  &:hover { opacity: 0.85; }
`;

const EmptyState = styled.div`text-align: center; padding: 60px 20px; color: #9ca3af;
  h2 { font-size: 18px; margin: 0 0 8px; }
  p { font-size: 14px; margin: 0; }`;

const nextStatus: Record<string, string> = { pending: 'preparing', preparing: 'ready', ready: 'served' };
const nextLabel: Record<string, string> = { pending: '▶ Inizia', preparing: '✓ Pronto', ready: '🍽 Servito' };
const statusLabel: Record<string, string> = { pending: 'In attesa', preparing: '🔥 Prep.', ready: '✅ Pronto', served: 'Servito' };

function elapsed(iso: string) {
  const diff = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (diff < 1) return 'ora';
  if (diff === 1) return '1 min fa';
  return `${diff} min fa`;
}

const KitchenView: React.FC = () => {
  const { on } = useSocketEvents();
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [, setTick] = useState(0);

  const reload = useCallback(async () => {
    const res = await orderService.getActive();
    setOrders(res.data?.orders || []);
  }, []);

  useEffect(() => { reload().finally(() => setLoading(false)); }, [reload]);

  // Tick ogni minuto per aggiornare i tempi
  useEffect(() => {
    const t = setInterval(() => setTick(n => n + 1), 60000);
    return () => clearInterval(t);
  }, []);

  // Aggiornamenti real-time
  useEffect(() => {
    const cleanups = [
      on('order:new', reload),
      on('order:updated', reload),
      on('order:item_added', reload),
      on('order:item_updated', reload),
      on('order:item_removed', reload),
      on('order:sent', reload),
    ];
    return () => cleanups.forEach(fn => fn());
  }, [on, reload]);

  const handleAdvanceItem = async (item: OrderItem) => {
    const next = nextStatus[item.status];
    if (!next) return;
    await orderService.updateItemStatus(item.id, next);
    reload();
  };

  const handleAdvanceCourse = async (orderId: number, course: number, items: OrderItem[]) => {
    // Calcola il prossimo status da applicare a tutta la portata
    const hasAnyPending = items.some(i => i.status === 'pending');
    const hasAnyPreparing = items.some(i => i.status === 'preparing' || i.status === 'pending');
    const next = hasAnyPending ? 'preparing' : hasAnyPreparing ? 'ready' : 'served';
    await orderService.advanceCourse(orderId, course, next);
    reload();
  };

  // Calcola il pulsante di portata in base allo stato minimo degli articoli
  const getCourseNextLabel = (items: OrderItem[]) => {
    if (items.some(i => i.status === 'pending')) return { status: 'preparing', label: '▶ Inizia portata' };
    if (items.some(i => i.status === 'preparing')) return { status: 'ready', label: '✓ Portata pronta' };
    return { status: 'served', label: '🍽 Servita' };
  };

  // Ordini con almeno una voce inviata (non bozza, non servita)
  const visible = orders.filter(o => o.items.some(i => i.status !== 'served' && i.status !== 'draft'));

  if (loading) return <Page><p style={{ color: '#6b7280' }}>Caricamento...</p></Page>;

  return (
    <Page>
      <Header>
        <Title>Vista Cucina</Title>
        <LiveDot>Aggiornamento in tempo reale — {visible.length} {visible.length === 1 ? 'ordine attivo' : 'ordini attivi'}</LiveDot>
      </Header>

      {visible.length === 0 ? (
        <EmptyState>
          <h2>Nessun ordine in corso</h2>
          <p>Quando il cameriere prende un ordine apparirà qui automaticamente.</p>
        </EmptyState>
      ) : (
        <Grid>
          {visible.map(order => {
            const kitchenItems = order.items.filter(i => i.status !== 'served' && i.status !== 'draft');
            const hasReady = kitchenItems.some(i => i.status === 'ready');
            // Raggruppa per portata
            const courses = Array.from(new Set(kitchenItems.map(i => i.course))).sort((a, b) => a - b);
            return (
              <OrderCard key={order.id} $hasReady={hasReady}>
                <OrderCardHeader>
                  <TableInfo>
                    <TableLabel>{order.room_name} — Tavolo {order.table_name}</TableLabel>
                    <TableSub>
                      {kitchenItems.length} {kitchenItems.length === 1 ? 'voce' : 'voci'} ·{' '}
                      {order.waiter_name && `Cameriere: ${order.waiter_name}`}
                    </TableSub>
                  </TableInfo>
                  <TimeLabel>{elapsed(order.created_at)}</TimeLabel>
                </OrderCardHeader>

                <ItemsBlock>
                  {courses.map(courseNum => {
                    const courseItems = kitchenItems.filter(i => i.course === courseNum);
                    const courseName = courseItems[0]?.course_name;
                    return (
                      <div key={courseNum}>
                        {(() => {
                          const { status: nextSt, label: nextLbl } = getCourseNextLabel(courseItems);
                          return (
                            <CourseRow>
                              <CourseLabel>
                                Portata {courseNum}{courseName ? ` · ${courseName}` : ''}
                              </CourseLabel>
                              <CourseBtn $next={nextSt} onClick={() => handleAdvanceCourse(order.id, courseNum, courseItems)}>
                                {nextLbl}
                              </CourseBtn>
                            </CourseRow>
                          );
                        })()}
                        {courseItems.map(item => (
                          <ItemLine key={item.id} $status={item.status}>
                            <ItemQty>×{item.quantity}</ItemQty>
                            <div style={{ flex: 1 }}>
                              <ItemName>{item.product_name}</ItemName>
                              {item.notes && <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>📝 {item.notes}</div>}
                            </div>
                            {nextStatus[item.status] && (
                              <StatusBtn $status={item.status} onClick={() => handleAdvanceItem(item)}>
                                {nextLabel[item.status]}
                              </StatusBtn>
                            )}
                          </ItemLine>
                        ))}
                      </div>
                    );
                  })}
                  {order.items.filter(i => i.status === 'served').length > 0 && (
                    <div style={{ fontSize: 12, color: '#d1d5db', textAlign: 'right', marginTop: 4 }}>
                      +{order.items.filter(i => i.status === 'served').length} già serviti
                    </div>
                  )}
                </ItemsBlock>
              </OrderCard>
            );
          })}
        </Grid>
      )}
    </Page>
  );
};

export default KitchenView;
